import styles from './DoctorAddMedicineToOrderForm.module.css';

import classNames from 'classnames';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FC, useCallback, useEffect } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { IMaskInput } from 'react-imask';
import { toast } from 'react-toastify';

import { PeopleApi } from '../../api';
import { AddressResponse } from '../../model/Address';
import { AddressAutocomplete, Modal } from '../molecules';

interface FormData extends AddressResponse {
  address: AddressResponse | null;
  date?: string;
}

interface Props {
  isOpen: boolean;
  onClose(): void;
  patientId: number | null;
  initialData?: AddressResponse | null;
  onAddressSelect: (address: AddressResponse) => void;
}

export const CreateAddressModal: FC<Props> = ({
  isOpen,
  onClose,
  onAddressSelect,
  patientId,
  initialData = null,
}) => {
  const queryClient = useQueryClient();

  const {
    control,
    register,
    handleSubmit,
    setValue,
    getValues,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    defaultValues: {
      address: null,
      address_text: '',
      index: '',
      country_name: '',
      region_name: '',
      city_name: '',
      street_name: '',
      house: '',
      flat: '',
      people_main_id: patientId ?? null,
      date: '',
    },
  });

  useEffect(() => {
    if (!isOpen) return;

    console.log('initialData on modal open:', initialData);

    if (initialData) {
      reset({
        address: initialData,
        address_text: initialData.address_text || '',
        index: initialData.index || '',
        country_name: initialData.country_name || '',
        region_name: initialData.region_name || '',
        city_name: initialData.city_name || '',
        street_name: initialData.street_name || '',
        house: initialData.house || '',
        flat: initialData.flat || '',
        people_main_id: initialData.people_main_id ?? patientId,
      });
    } else {
      reset({
        address: null,
        address_text: '',
        index: '',
        country_name: '',
        region_name: '',
        city_name: '',
        street_name: '',
        house: '',
        flat: '',
        people_main_id: patientId ?? null,
      });
    }
  }, [isOpen, initialData, patientId, reset]);

  const watchedFields = watch([
    'region_name',
    'city_name',
    'street_name',
    'house',
    'flat',
  ]);

  useEffect(() => {
    const [region_name, city_name, street_name, house, flat] = watchedFields;
    const formattedSource = [
      region_name,
      city_name,
      street_name,
      house ? `д. ${house}` : '',
      flat ? `кв. ${flat}` : '',
    ]
      .filter(Boolean)
      .join(', ');

    setValue('address_text', formattedSource);

    const currentAddress = getValues('address');

    setValue('address', {
      id: currentAddress?.id || 0,
      address_text: formattedSource,
      index: currentAddress?.index || '',
      country_name: currentAddress?.country_name || '',
      region_name: region_name || '',
      city_name: city_name || '',
      street_name: street_name || '',
      house: house || '',
      flat: flat || '',
      people_main_id: currentAddress?.people_main_id ?? patientId ?? null,
    });
  }, [watchedFields, setValue, getValues, patientId]);

  const {
    data: addressList = { total: 0, items: [] },
    isLoading: isAddressesLoading,
  } = useQuery({
    queryKey: ['addresses', patientId],
    queryFn: () => PeopleApi.getAddress(patientId),
    enabled: isOpen && !!patientId,
  });

  const { mutateAsync: createAddress } = useMutation(
    (data: AddressResponse) => PeopleApi.createPatientsAddress(data),
    {
      onSuccess: (response) => {
        toast.success('Адрес успешно добавлен');
        queryClient.invalidateQueries(['addresses', patientId]);
        onAddressSelect(response.data);
        reset();
        onClose();
      },
      onError: (error: any) => {
        console.error(error);
        toast.error('Ошибка при сохранении адреса. Проверьте данные');
      },
    },
  );

  const onSubmit: SubmitHandler<FormData> = useCallback(
    async (data) => {
      if (!patientId) {
        toast.error('Не указан ID пациента');
        return;
      }

      if (data.address && data.address.id !== 0) {
        onAddressSelect(data.address);
        onClose();
        return;
      }

      const addressToSend: AddressResponse = {
        ...data,
        people_main_id: patientId,
      };
      await createAddress(addressToSend);
    },
    [createAddress, patientId],
  );

  const onAddressChange = (address: AddressResponse) => {
    setValue('address', address);
    setValue('address_text', address.address_text || '');
    setValue('region_name', address.region_name || '');
    setValue('city_name', address.city_name || '');
    setValue('street_name', address.street_name || '');
    setValue('house', address.house || '');
    setValue('flat', address.flat || '');
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="flex max-h-[90vh] min-h-[300px] flex-col overflow-hidden rounded-[16px] bg-brand-6 p-[24px]">
        {!isAddressesLoading && addressList.items.length > 0 && (
          <div className="mb-[16px] pt-[24px]">
            <div className={`${styles['scrollbar-none']} overflow-x-auto`}>
              <div className="flex gap-[12px] pb-[20px]">
                {addressList.items.map((addr) => {
                  const label =
                    [
                      addr.city_name,
                      addr.street_name,
                      addr.house ? `д. ${addr.house}` : '',
                      addr.flat ? `кв. ${addr.flat}` : '',
                    ]
                      .filter(Boolean)
                      .join(', ') || addr.address_text;
                  if (!label) return null;
                  return (
                    <label key={addr.id} className={styles.radio}>
                      <input
                        className="hidden"
                        type="radio"
                        name="saved-address"
                        onChange={() => onAddressChange(addr)}
                        checked={getValues('address')?.id === addr.id}
                      />
                      <span className="whitespace-nowrap leading-normal">
                        {label}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex grow flex-col overflow-auto pt-[24px]"
        >
          <div className="md-[4px] mb-[12px] w-full">
            <Controller
              control={control}
              name="address"
              rules={{
                required: true,
              }}
              render={({ field }) => (
                <AddressAutocomplete
                  value={field.value}
                  onChange={(address) => {
                    field.onChange(address);
                    onAddressChange(address);
                  }}
                />
              )}
            />
            {errors.address && (
              <p className="text-red-600 mt-[10px] text-sm">
                {errors.address.message}
              </p>
            )}
          </div>

          <div className="mb-[24px] grid grid-cols-1 gap-[12px] sm:grid-cols-2">
            <input
              {...register('region_name', { required: true })}
              className={classNames(
                'input',
                errors.region_name && 'input--invalid',
              )}
              placeholder="Регион"
            />
            <input
              {...register('city_name', { required: true })}
              className={classNames(
                'input',
                errors.region_name && 'input--invalid',
              )}
              placeholder="Город"
            />
            <input
              {...register('street_name', { required: true })}
              className={classNames(
                'input',
                errors.region_name && 'input--invalid',
              )}
              placeholder="Улица"
            />
            <input
              {...register('house', { required: true })}
              className={classNames(
                'input',
                errors.region_name && 'input--invalid',
              )}
              placeholder="Дом"
            />
            <input
              {...register('flat', { required: true })}
              className={classNames(
                'input',
                errors.region_name && 'input--invalid',
              )}
              placeholder="Квартира"
            />
            <Controller
              control={control}
              name="date"
              render={({ field }) => (
                <IMaskInput
                  mask="00.00.0000"
                  lazy={false}
                  overwrite
                  inputMode="numeric"
                  value={field.value || ''}
                  onAccept={(value: string) => field.onChange(value)}
                  className={classNames(
                    'input',
                    errors.region_name && 'input--invalid',
                  )}
                  placeholder="Дата (ДД.ММ.ГГГГ)"
                  unmask={false}
                />
              )}
            />
          </div>

          <button
            className="button button--primary mt-[24px] flex w-full items-center justify-between px-[20px] text-left"
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Сохраняем...' : 'Добавить адрес'}
          </button>
        </form>
      </div>
    </Modal>
  );
};
